import React, { useState, useEffect, useRef } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import CustomerCapabilityAssessment from '../CustomerCapabilityAssessment/CustomerCapabilityAssessment';
import {
  Shield,
  Network,
  Wifi,
  Computer,
  Cloud,
  Server,
  Link,
  X,
  Save,
  Trash2,
  BarChart3,
  Info,
  CheckCircle,
  Zap,
  Database,
  Lock,
  Activity,
  HardDrive,
  Scale,
  Users,
  ChevronRight
} from 'lucide-react';
import './Dashboard.css';
import apiClient from '../../lib/apiClient';

// Icon mapping for tech types
const techTypeIcons = {
  firewall: Shield,
  switch: Network,
  wireless: Wifi,
  software: Computer,
  cloud: Cloud,
  server: Server
};

// Icon mapping for connection types
const connectionTypeIcons = {
  connects_to: Link,
  depends_on: Zap,
  provides_data_to: Database,
  authenticates_with: Lock,
  monitors: Activity,
  backs_up_to: HardDrive,
  load_balances_to: Scale
};

function Dashboard({ selectedDepartmentId, selectedDepartmentName, selectedDepartment }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [canvasElements, setCanvasElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedElement, setDraggedElement] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [draggedElementId, setDraggedElementId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [connectionType, setConnectionType] = useState('connects_to');
  const [connectionDescription, setConnectionDescription] = useState('');
  const [connectionInstructions, setConnectionInstructions] = useState('');
  const [previewConnection, setPreviewConnection] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [componentCapabilities, setComponentCapabilities] = useState({});
  const [showCapabilityAssessment, setShowCapabilityAssessment] = useState(false);

  const techTypes = [
    { id: 'firewall', name: 'Firewall', color: '#ef4444' },
    { id: 'switch', name: 'Switch', color: '#3b82f6' },
    { id: 'wireless', name: 'Wireless', color: '#10b981' },
    { id: 'software', name: 'Suite of Software', color: '#8b5cf6' },
    { id: 'cloud', name: 'Cloud Instance', color: '#f59e0b' },
    { id: 'server', name: 'Server', color: '#6b7280' }
  ];

  // Fetch connection types and canvas data when component mounts or department changes
  useEffect(() => {
    fetchConnectionTypes();
  }, []);

  useEffect(() => {
    if (selectedDepartmentId) {
      fetchDepartmentCanvas(selectedDepartmentId);
      fetchComponentCapabilities();
    }
  }, [selectedDepartmentId]);

  const fetchConnectionTypes = async () => {
  console.log('📍 Fetching connection types...');
  
  try {
    // Use apiClient
    const data = await apiClient.get('/connection-types');
    
    console.log('✅ Connection types received:', data);
    
    const sortedData = data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setConnectionTypes(sortedData);
    
    if (sortedData.length > 0 && !connectionType) {
      setConnectionType(sortedData[0].type_id);
    }
  } catch (error) {
    console.error('❌ Error fetching connection types:', error);
  }
};

const fetchDepartmentCanvas = async (departmentId) => {
  try {
    console.log(`📍 Fetching canvas for department ${departmentId}...`);
    
    // Use apiClient instead of raw fetch
    const data = await apiClient.get(`/api/departments/${departmentId}/canvas`);
    
    console.log('✅ Canvas data received:', data);
    
    if (data && data.canvas_data) {
      const canvasData = typeof data.canvas_data === 'string' 
        ? JSON.parse(data.canvas_data) 
        : data.canvas_data;
      
      setCanvasElements(canvasData.elements || []);
      setConnections(canvasData.connections || []);
      setHasUnsavedChanges(false);
      
      console.log(`📊 Loaded ${(canvasData.elements || []).length} elements and ${(canvasData.connections || []).length} connections`);
    } else {
      console.log('No canvas data found, starting with empty canvas');
      setCanvasElements([]);
      setConnections([]);
    }
  } catch (error) {
    console.error('❌ Error fetching department canvas:', error);
    setCanvasElements([]);
    setConnections([]);
  }
};
  // Fetch capability assessments and map to components
  const fetchComponentCapabilities = async () => {
  if (!selectedDepartmentId) return;
  
  try {
    console.log(`📍 Fetching capabilities for department ${selectedDepartmentId}...`);
    
    // Use apiClient instead of raw fetch
    const assessments = await apiClient.get(`/departments/${selectedDepartmentId}/capability-assessments`);
    
    console.log('✅ Capabilities received:', assessments);
    
    // Create a map of component names to capabilities
    const capabilityMap = {};
    
    assessments.forEach(assessment => {
      if (assessment.has_capability && assessment.deployment_status === 'deployed' && assessment.notes) {
        // Extract linked components from notes
        const linkedMatch = assessment.notes.match(/\[Linked to: (.*?)\]/);
        if (linkedMatch) {
          const componentNames = linkedMatch[1].split(', ');
          componentNames.forEach(name => {
            if (!capabilityMap[name]) {
              capabilityMap[name] = [];
            }
            capabilityMap[name].push({
              name: assessment.capability_name || 'Unknown Capability',
              solution: assessment.solution_used
            });
          });
        }
      }
    });
    
    setComponentCapabilities(capabilityMap);
  } catch (error) {
    console.error('❌ Error fetching component capabilities:', error);
  }
};

  useEffect(() => {
    console.log('hasUnsavedChanges changed to:', hasUnsavedChanges);
  }, [hasUnsavedChanges]);

const saveArchitecture = async (elements = canvasElements, conn = connections, force = false) => {
  console.log('saveArchitecture called:', { 
    hasUnsavedChanges, 
    force, 
    selectedDepartmentId,
    elementsCount: elements.length,
    connectionsCount: conn.length 
  });
  
  if (!selectedDepartmentId) {
    console.error('Cannot save: No department selected');
    return;
  }
  
  if (!hasUnsavedChanges && !force) {
    console.log('No changes to save');
    return;
  }

  try {
    const saveData = {
      canvas_data: {
        elements: elements,
        connections: conn
      }
    };
    
    console.log('💾 Saving canvas data:', saveData);
    
    // Use apiClient instead of raw fetch
    const result = await apiClient.post(`/api/departments/${selectedDepartmentId}/canvas`, saveData);
    
    console.log('✅ Canvas saved successfully:', result);
    setHasUnsavedChanges(false);
    
    // Optional: Show success message
    // You could add a toast notification here
  } catch (error) {
    console.error('❌ Error saving canvas:', error);
    // Keep hasUnsavedChanges true so user can retry
    alert(`Failed to save canvas: ${error.message}`);
  }
};

  // Add autosave functionality
  useEffect(() => {
    if (!hasUnsavedChanges || !selectedDepartmentId) return;
    
    // Debounce autosave - wait 3 seconds after last change
    const autosaveTimer = setTimeout(() => {
      console.log('Autosaving changes...');
      saveArchitecture();
    }, 3000);
    
    return () => clearTimeout(autosaveTimer);
  }, [canvasElements, connections]); // Re-run when data changes

  // Connection instructions
  useEffect(() => {
    if (isConnecting) {
      if (!connectionStart) {
        setConnectionInstructions('Click on the first component to start connecting');
      } else {
        setConnectionInstructions('Click on the second component to complete the connection');
      }
    } else {
      setConnectionInstructions('');
    }
  }, [isConnecting, connectionStart]);

  // Utility functions (getEdgePoint, createOrthogonalPath, etc. remain the same)
  const getEdgePoint = (fromElement, toElement, isSource = true) => {
    const element = isSource ? fromElement : toElement;
    const otherElement = isSource ? toElement : fromElement;
    
    const centerX = element.x + element.width / 2;
    const centerY = element.y + element.height / 2;
    const otherCenterX = otherElement.x + otherElement.width / 2;
    const otherCenterY = otherElement.y + otherElement.height / 2;
    
    const dx = otherCenterX - centerX;
    const dy = otherCenterY - centerY;
    
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    
    let edgeX, edgeY;
    
    if (absDx > absDy) {
      if (dx > 0) {
        edgeX = element.x + element.width;
        edgeY = centerY;
      } else {
        edgeX = element.x;
        edgeY = centerY;
      }
    } else {
      if (dy > 0) {
        edgeX = centerX;
        edgeY = element.y + element.height;
      } else {
        edgeX = centerX;
        edgeY = element.y;
      }
    }
    
    return { x: edgeX, y: edgeY };
  };

  const createOrthogonalPath = (fromPoint, toPoint, fromElement, toElement) => {
    const cornerRadius = 10;
    const dx = Math.abs(fromPoint.x - toPoint.x);
    const dy = Math.abs(fromPoint.y - toPoint.y);

    if (dx < 40 || dy < 40) {
      return `M ${fromPoint.x} ${fromPoint.y} L ${toPoint.x} ${toPoint.y}`;
    }

    const offset = Math.min(30, Math.max(10, Math.min(dx, dy) / 2));
    
    let fromDir;
    
    if (Math.abs(fromPoint.x - fromElement.x) < 1) {
      fromDir = 'left';
    } else if (Math.abs(fromPoint.x - (fromElement.x + fromElement.width)) < 1) {
      fromDir = 'right';
    } else if (Math.abs(fromPoint.y - fromElement.y) < 1) {
      fromDir = 'top';
    } else {
      fromDir = 'bottom';
    }
    
    let toDir;
    
    if (Math.abs(toPoint.x - toElement.x) < 1) {
      toDir = 'left';
    } else if (Math.abs(toPoint.x - (toElement.x + toElement.width)) < 1) {
      toDir = 'right';
    } else if (Math.abs(toPoint.y - toElement.y) < 1) {
      toDir = 'top';
    } else {
      toDir = 'bottom';
    }
    
    let path = `M ${fromPoint.x} ${fromPoint.y}`;
    
    const waypoints = [];
    
    let wp1 = { x: fromPoint.x, y: fromPoint.y };
    if (fromDir === 'left') wp1.x -= offset;
    else if (fromDir === 'right') wp1.x += offset;
    else if (fromDir === 'top') wp1.y -= offset;
    else if (fromDir === 'bottom') wp1.y += offset;
    waypoints.push(wp1);
    
    let wp4 = { x: toPoint.x, y: toPoint.y };
    if (toDir === 'left') wp4.x -= offset;
    else if (toDir === 'right') wp4.x += offset;
    else if (toDir === 'top') wp4.y -= offset;
    else if (toDir === 'bottom') wp4.y += offset;
    
    if ((fromDir === 'left' || fromDir === 'right') && (toDir === 'left' || toDir === 'right')) {
      const midX = (wp1.x + wp4.x) / 2;
      waypoints.push({ x: midX, y: wp1.y });
      waypoints.push({ x: midX, y: wp4.y });
    } else if ((fromDir === 'top' || fromDir === 'bottom') && (toDir === 'top' || toDir === 'bottom')) {
      const midY = (wp1.y + wp4.y) / 2;
      waypoints.push({ x: wp1.x, y: midY });
      waypoints.push({ x: wp4.x, y: midY });
    } else {
      if (fromDir === 'left' || fromDir === 'right') {
        waypoints.push({ x: wp4.x, y: wp1.y });
      } else {
        waypoints.push({ x: wp1.x, y: wp4.y });
      }
    }
    
    waypoints.push(wp4);
    
    for (let i = 0; i < waypoints.length; i++) {
      const curr = waypoints[i];
      const next = waypoints[i + 1];
      const prev = i > 0 ? waypoints[i - 1] : fromPoint;
      
      if (i === 0) {
        path += ` L ${curr.x} ${curr.y}`;
      } else if (i < waypoints.length - 1 && next) {
        const dx1 = curr.x - prev.x;
        const dy1 = curr.y - prev.y;
        const dx2 = next.x - curr.x;
        const dy2 = next.y - curr.y;
        
        if (dx1 !== 0) {
          path += ` L ${curr.x - Math.sign(dx1) * cornerRadius} ${curr.y}`;
        } else {
          path += ` L ${curr.x} ${curr.y - Math.sign(dy1) * cornerRadius}`;
        }
        
        path += ` Q ${curr.x} ${curr.y}`;
        
        if (dx2 !== 0) {
          path += ` ${curr.x + Math.sign(dx2) * cornerRadius} ${curr.y}`;
        } else {
          path += ` ${curr.x} ${curr.y + Math.sign(dy2) * cornerRadius}`;
        }
      } else if (i === waypoints.length - 1) {
        path += ` L ${curr.x} ${curr.y}`;
        path += ` L ${toPoint.x} ${toPoint.y}`;
      }
    }
    
    return path;
  };

  // Mouse tracking for preview connection
  const handleCanvasMouseMove = (e) => {
    if (isConnecting && connectionStart) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setMousePosition({ x, y });
      
      const fromEdge = getEdgePoint(connectionStart, { 
        x: x - 50, 
        y: y - 25, 
        width: 100, 
        height: 50 
      }, true);
      
      setPreviewConnection({
        fromPoint: fromEdge,
        toPoint: { x, y },
        fromElement: connectionStart
      });
    }
  };

  // Canvas handlers
  const handleDragStart = (e, techType) => {
    if (isConnecting) return;
    setDraggedElement(techType);
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Fix handleDrop to properly set unsaved changes
  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggedElement || isConnecting) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate element dimensions
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = '11px';
    tempDiv.style.fontWeight = '500';
    tempDiv.textContent = draggedElement.name;
    document.body.appendChild(tempDiv);
    
    const textWidth = tempDiv.offsetWidth;
    document.body.removeChild(tempDiv);
    
    const padding = 24;
    const iconSize = 24;
    const gap = 4;
    const minWidth = 80;
    const width = Math.max(minWidth, textWidth + padding * 2);
    const height = iconSize + gap + 16 + padding;

    const newElement = {
      id: Date.now(),
      type: draggedElement.id,
      name: draggedElement.name,
      color: draggedElement.color,
      x: x - width / 2,
      y: y - height / 2,
      width: width,
      height: height
    };

    // Update both states together
    setCanvasElements(prev => [...prev, newElement]);
    setHasUnsavedChanges(true);
    setDraggedElement(null);
    setIsDragging(false);
    
    console.log('Element dropped, unsaved changes:', true);
  };

  // Element dragging handlers
  const handleElementMouseDown = (e, element) => {
    if (isConnecting) return;
    
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - element.x;
    const offsetY = e.clientY - rect.top - element.y;
    
    setIsDraggingElement(true);
    setDraggedElementId(element.id);
    setDragOffset({ x: offsetX, y: offsetY });
    setSelectedElement(element);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingElement || !draggedElementId) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragOffset.x;
    const newY = e.clientY - rect.top - dragOffset.y;
    
    const element = canvasElements.find(el => el.id === draggedElementId);
    if (!element) return;
    
    const constrainedX = Math.max(0, Math.min(newX, rect.width - element.width));
    const constrainedY = Math.max(0, Math.min(newY, rect.height - element.height));
    
    setCanvasElements(prev => prev.map(el => 
      el.id === draggedElementId 
        ? { ...el, x: constrainedX, y: constrainedY }
        : el
    ));
    
    setHasUnsavedChanges(true);
  };

  const handleMouseUp = () => {
    setIsDraggingElement(false);
    setDraggedElementId(null);
    setDragOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (isDraggingElement) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingElement, draggedElementId, dragOffset, canvasElements]);

  // Element click handler
  const handleElementClick = (element, e) => {
    e.stopPropagation();
    
    if (isConnecting) {
      if (!connectionStart) {
        setConnectionStart(element);
        setSelectedElement(element);
      } else if (connectionStart.id !== element.id) {
        setPendingConnection({
          from: connectionStart,
          to: element
        });
        setShowConnectionModal(true);
        setPreviewConnection(null);
      } else {
        console.log('Cannot connect element to itself');
      }
    } else {
      setSelectedElement(element);
    }
  };

  // Connection toggle
  const toggleConnectionMode = () => {
    if (isConnecting) {
      setIsConnecting(false);
      setConnectionStart(null);
      setPreviewConnection(null);
      setSelectedElement(null);
    } else {
      setIsConnecting(true);
      setSelectedElement(null);
      setSelectedConnection(null);
    }
  };

  // Handle connection creation with type
  const handleCreateConnection = () => {
    if (!pendingConnection) return;

    const connectionTypeData = connectionTypes.find(ct => ct.type_id === connectionType);
    
    const newConnection = {
      id: Date.now(),
      from: pendingConnection.from.id,
      to: pendingConnection.to.id,
      type: connectionType,
      description: connectionDescription,
      color: connectionTypeData?.color || '#6b7280',
      name: connectionTypeData?.name || 'Connection',
      icon: connectionTypeData?.icon || 'link'
    };

    const updatedConnections = [...connections, newConnection];
    setConnections(updatedConnections);
    setConnectionStart(null);
    setIsConnecting(false);
    setSelectedElement(null);
    setHasUnsavedChanges(true);
    setPreviewConnection(null);
    saveArchitecture(canvasElements, updatedConnections, true);
    
    setShowConnectionModal(false);
    setPendingConnection(null);
    setConnectionType(connectionTypes[0]?.type_id || 'connects_to');
    setConnectionDescription('');
  };

  // Cancel connection creation
  const handleCancelConnection = () => {
    setShowConnectionModal(false);
    setPendingConnection(null);
    setConnectionType(connectionTypes[0]?.type_id || 'connects_to');
    setConnectionDescription('');
    setConnectionStart(null);
    setIsConnecting(false);
    setSelectedElement(null);
    setPreviewConnection(null);
  };

  const handleCanvasClick = (e) => {
    if (isConnecting) {
      return;
    }
    setSelectedElement(null);
    setSelectedConnection(null);
  };

  const handleConnectionClick = (connection, e) => {
    e.stopPropagation();
    if (isConnecting) return;
    setSelectedConnection(connection);
    setSelectedElement(null);
  };

  const deleteElement = (elementId) => {
    setCanvasElements(prev => prev.filter(el => el.id !== elementId));
    setConnections(prev => prev.filter(conn => conn.from !== elementId && conn.to !== elementId));
    setSelectedElement(null);
    setHasUnsavedChanges(true);
    
    if (connectionStart?.id === elementId) {
      setConnectionStart(null);
      setIsConnecting(false);
      setPreviewConnection(null);
    }
    
    console.log('Element deleted, unsaved changes:', true);
  };

  const deleteConnection = (connectionId) => {
    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
    setSelectedConnection(null);
    setHasUnsavedChanges(true);
    console.log('Connection deleted, unsaved changes:', true);
  };

  // Clear canvas function
  const clearCanvas = async () => {
    if (canvasElements.length > 0 || connections.length > 0) {
      if (await confirmDialog('Are you sure you want to clear the canvas? This will remove all elements and connections.')) {
        setCanvasElements([]);
        setConnections([]);
        setSelectedElement(null);
        setConnectionStart(null);
        setIsConnecting(false);
        setPreviewConnection(null);
        setHasUnsavedChanges(true);
        console.log('Canvas cleared, unsaved changes:', true);
      }
    }
  };

  // Add keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges) {
          console.log('Keyboard save triggered');
          saveArchitecture();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, canvasElements, connections]);

  // Debug helper
  useEffect(() => {
    console.log('Dashboard state:', {
      hasUnsavedChanges,
      elementsCount: canvasElements.length,
      connectionsCount: connections.length,
      selectedDepartmentId
    });
  }, [hasUnsavedChanges, canvasElements.length, connections.length, selectedDepartmentId]);

  // Show the capability assessment overlay
  if (showCapabilityAssessment && selectedDepartmentId) {
    return (
      <CustomerCapabilityAssessment
        customerId={selectedDepartmentId}
        customerName={selectedDepartmentName}
        onClose={() => {
          setShowCapabilityAssessment(false);
          // Refresh capabilities when closing assessment
          fetchComponentCapabilities();
        }}
      />
    );
  }

  return (
    <div className="content-area">
      <h2 className="page-title">Technology Architecture Dashboard</h2>
      {selectedDepartmentId ? (
        <>
          <div className="dashboard-header">
            <div className="flex items-center gap-4">
              <h3 className="dashboard-customer-title">Department: {selectedDepartmentName}</h3>
              {selectedDepartment?.department_number && (
                <span className="customer-meta">Dept#: {selectedDepartment.department_number}</span>
              )}
              {selectedDepartment?.agency_level && (
                <span className="customer-meta hidden lg:inline">Level: {selectedDepartment.agency_level}</span>
              )}
              {selectedDepartment?.parent_department_id && (
                <span className="customer-meta hidden lg:inline">Parent: #{selectedDepartment.parent_department_id}</span>
              )}
            </div>
            <div className="dashboard-controls">
              <button
                className="btn flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors text-sm font-medium border border-indigo-200"
                onClick={() => navigate('/workspace')}
                title="View customer org chart"
              >
                <Users size={14} />
                <span className="hidden sm:inline">Org Chart</span>
                <ChevronRight size={12} className="hidden sm:inline" />
              </button>
              <button
                className="btn flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 transition-colors text-sm font-medium border border-purple-200"
                onClick={() => setShowCapabilityAssessment(true)}
              >
                <BarChart3 size={14} />
                <span className="hidden sm:inline">Capability Assessment</span>
              </button>
              <button 
                className={`btn flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors text-sm font-medium border ${
                  isConnecting 
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200' 
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200'
                }`}
                onClick={toggleConnectionMode}
              >
                {isConnecting ? (
                  <>
                    <X size={14} />
                    <span className="hidden sm:inline">Cancel Connection</span>
                  </>
                ) : (
                  <>
                    <Link size={14} />
                    <span className="hidden sm:inline">Connect Components</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  console.log('Manual save triggered');
                  saveArchitecture();
                }}
                disabled={!hasUnsavedChanges}
                className={`btn flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors text-sm font-medium border relative ${
                  hasUnsavedChanges
                    ? 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200 cursor-pointer'
                    : 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                }`}
                title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}
              >
                <Save size={14} />
                <span className="hidden sm:inline">{hasUnsavedChanges ? 'Save Layout' : 'Saved'}</span>
              </button>
              <button 
                onClick={clearCanvas}
                className=" btn flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-md hover:bg-red-100 transition-colors text-sm font-medium border border-red-200"
              >
                <Trash2 size={14} />
                <span className="hidden sm:inline">Clear Canvas</span>
              </button>
            </div>
          </div>

          {connectionInstructions && (
            <div className="connection-instructions">
              <div className="connection-instructions-content">
                <Info size={20} className="connection-instructions-icon" />
                <span className="connection-instructions-text">{connectionInstructions}</span>
                {connectionStart && (
                  <span className="connection-start-badge">
                    Starting from: <strong>{connectionStart.name}</strong>
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="dashboard-container">
            <div className="tech-palette">
              <h4 className="tech-palette-title">Technology Components</h4>
              <div className="tech-items">
                {techTypes.map(tech => {
                  const Icon = techTypeIcons[tech.id];
                  return (
                    <div
                      key={tech.id}
                      className={`tech-item tech-item-${tech.id} ${isConnecting ? 'disabled' : ''}`}
                      draggable={!isConnecting}
                      onDragStart={(e) => handleDragStart(e, tech)}
                    >
                      <Icon size={20} className="tech-item-icon" style={{ color: tech.color }} />
                      <span className="tech-item-name">{tech.name}</span>
                    </div>
                  );
                })}
              </div>
              <p className="tech-palette-hint">
                {isConnecting 
                  ? 'Connection mode active - click elements to connect them' 
                  : 'Drag components to the canvas to build your architecture'
                }
              </p>

              {isConnecting && connectionTypes.length > 0 && (
                <div className="connection-types-reference">
                  <h5 className="connection-types-title">Connection Types</h5>
                  <div className="connection-types-list">
                    {connectionTypes.slice(0, 4).map(type => {
                      const Icon = connectionTypeIcons[type.type_id] || Link;
                      return (
                        <div key={type.id} className="connection-type-item">
                          <Icon 
                            size={14} 
                            className="connection-type-item-icon"
                            style={{ color: type.color }} 
                          />
                          <span className="connection-type-item-name">{type.name}</span>
                        </div>
                      );
                    })}
                    {connectionTypes.length > 4 && (
                      <div className="connection-type-item">
                        <span className="text-xs text-gray-500">+{connectionTypes.length - 4} more...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="canvas-container">
              <div
                ref={canvasRef}
                className={`canvas ${isConnecting ? 'connecting' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasMouseMove}
              >
                <svg className="canvas-svg">
                  {previewConnection && (
                    <path
                      d={`M ${previewConnection.fromPoint.x} ${previewConnection.fromPoint.y} L ${previewConnection.toPoint.x} ${previewConnection.toPoint.y}`}
                      stroke="#f59e0b"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      fill="none"
                      opacity="0.5"
                      className="connection-preview"
                    />
                  )}
                  
                  {connections.map(conn => {
                    const fromElement = canvasElements.find(el => el.id === conn.from);
                    const toElement = canvasElements.find(el => el.id === conn.to);
                    
                    if (!fromElement || !toElement) return null;
                    
                    const fromPoint = getEdgePoint(fromElement, toElement, true);
                    const toPoint = getEdgePoint(toElement, fromElement);
                    const path = createOrthogonalPath(fromPoint, toPoint, fromElement, toElement);
                    
                    const isSelected = selectedConnection?.id === conn.id;
                    
                    let iconPosition;
                    
                    const pathCommands = path.match(/[MLQ]\s*[\d.-]+\s*[\d.-]+/g);
                    if (pathCommands && pathCommands.length > 2) {
                      const midIndex = Math.floor(pathCommands.length / 2);
                      const midCommand = pathCommands[midIndex];
                      const coords = midCommand.match(/[\d.-]+/g);
                      if (coords && coords.length >= 2) {
                        iconPosition = {
                          x: parseFloat(coords[coords.length - 2]),
                          y: parseFloat(coords[coords.length - 1])
                        };
                      } else {
                        iconPosition = {
                          x: (fromPoint.x + toPoint.x) / 2,
                          y: (fromPoint.y + toPoint.y) / 2
                        };
                      }
                    } else {
                      iconPosition = {
                        x: (fromPoint.x + toPoint.x) / 2,
                        y: (fromPoint.y + toPoint.y) / 2
                      };
                    }
                    
                    const Icon = connectionTypeIcons[conn.type] || Link;
                    
                    return (
                      <g key={conn.id}>
                        <path
                          d={path}
                          stroke={conn.color || '#6b7280'}
                          strokeWidth={isSelected ? "2.5" : "2"}
                          fill="none"
                          opacity="0.4"
                          className="connection-line"
                          onClick={(e) => handleConnectionClick(conn, e)}
                        />
                        <defs>
                          <marker
                            id={`arrowhead-${conn.id}`}
                            markerWidth="10"
                            markerHeight="7"
                            refX="9"
                            refY="3.5"
                            orient="auto"
                          >
                            <polygon
                              points="0 0, 10 3.5, 0 7"
                              fill={conn.color || '#6b7280'}
                              opacity="0.4"
                            />
                          </marker>
                        </defs>
                        <path
                          d={path}
                          stroke={conn.color || '#6b7280'}
                          strokeWidth={isSelected ? "2.5" : "2"}
                          fill="none"
                          opacity="0.4"
                          markerEnd={`url(#arrowhead-${conn.id})`}
                          className="connection-line"
                          onClick={(e) => handleConnectionClick(conn, e)}
                        />
                        <circle
                          cx={iconPosition.x}
                          cy={iconPosition.y}
                          r="10"
                          fill={conn.color || '#6b7280'}
                          opacity="0.3"
                          className="connection-icon-circle"
                          onClick={(e) => handleConnectionClick(conn, e)}
                        />
                        <g transform={`translate(${iconPosition.x - 6}, ${iconPosition.y - 6})`}>
                          <Icon 
                            size={12} 
                            color="white" 
                            style={{ pointerEvents: 'none' }}
                          />
                        </g>
                        {isSelected && (
                          <g>
                            <circle
                              cx={iconPosition.x + 15}
                              cy={iconPosition.y - 15}
                              r="8"
                              fill="#ef4444"
                              className="connection-delete-circle"
                              onClick={() => deleteConnection(conn.id)}
                            />
                            <text
                              x={iconPosition.x + 15}
                              y={iconPosition.y - 14}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="white"
                              fontSize="12"
                              fontWeight="bold"
                              pointerEvents="none"
                            >
                              ×
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </svg>

                {canvasElements.map(element => {
                  const Icon = techTypeIcons[element.type];
                  return (
                    <div
                      key={element.id}
                      className={`canvas-element canvas-element-${element.type} ${
                        selectedElement?.id === element.id ? 'selected' : ''
                      } ${
                        connectionStart?.id === element.id ? 'connection-start' : ''
                      } ${
                        isConnecting ? 'connecting' : ''
                      } ${
                        isDraggingElement && draggedElementId === element.id ? 'dragging' : ''
                      } ${
                        componentCapabilities[element.name] ? 'has-capabilities' : ''
                      }`}
                      data-element-type={element.type}
                      style={{
                        left: `${element.x}px`,
                        top: `${element.y}px`,
                        width: `${element.width}px`,
                        height: `${element.height}px`
                      }}
                      onMouseDown={(e) => !isConnecting && handleElementMouseDown(e, element)}
                      onClick={(e) => handleElementClick(element, e)}
                    >
                      <Icon size={24} className="element-icon" />
                      <span className="element-name">{element.name}</span>
                      
                      {/* Capability indicator badge */}
                      {componentCapabilities[element.name] && (
                        <div className="element-capabilities-badge" title={
                          `Implements: ${componentCapabilities[element.name].map(c => c.name).join(', ')}`
                        }>
                          <span className="capabilities-count">
                            {componentCapabilities[element.name].length}
                          </span>
                          <CheckCircle className="capabilities-icon" size={10} />
                        </div>
                      )}
                      
                      {selectedElement?.id === element.id && !isConnecting && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteElement(element.id);
                          }}
                          className="element-delete-button"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Show capability details for selected element */}
                {selectedElement && componentCapabilities[selectedElement.name] && (
                  <div className="selected-element-capabilities">
                    <h4>Implemented Capabilities:</h4>
                    <ul>
                      {componentCapabilities[selectedElement.name].map((cap, index) => (
                        <li key={index}>
                          <strong>{cap.name}</strong>
                          {cap.solution && <span> - {cap.solution}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {canvasElements.length === 0 && (
                  <div className="canvas-placeholder">
                    <h3 className="canvas-placeholder-title">Start Building Your Tech Stack</h3>
                    <p className="canvas-placeholder-text">
                      Drag technology components from the left panel onto this canvas to create your architecture diagram.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {showConnectionModal && (
            <div className="modal-overlay" onClick={handleCancelConnection}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3 className="modal-title">Define Connection</h3>
                <p className="modal-description">
                  Connect <strong>{pendingConnection?.from.name}</strong> to <strong>{pendingConnection?.to.name}</strong>
                </p>
                
                <div className="modal-form">
                  <label className="form-label">
                    Connection Type
                    <select 
                      value={connectionType} 
                      onChange={(e) => setConnectionType(e.target.value)}
                      className="form-select"
                    >
                      {connectionTypes.map(type => (
                        <option key={type.id} value={type.type_id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  
                  <div className="connection-type-preview">
                    <div 
                      className="connection-type-icon"
                      style={{ backgroundColor: connectionTypes.find(ct => ct.type_id === connectionType)?.color || '#6b7280' }}
                    >
                      {(() => {
                        const Icon = connectionTypeIcons[connectionType] || Link;
                        return <Icon size={20} />;
                      })()}
                    </div>
                    <p className="connection-type-description">
                      {connectionTypes.find(ct => ct.type_id === connectionType)?.description || ''}
                    </p>
                  </div>
                  
                  <label className="form-label">
                    Description (Optional)
                    <textarea 
                      value={connectionDescription}
                      onChange={(e) => setConnectionDescription(e.target.value)}
                      placeholder="Describe this connection..."
                      rows="3"
                      className="form-textarea"
                    />
                  </label>
                  
                  <div className="modal-actions">
                    <button 
                      onClick={handleCreateConnection}
                      className="btn px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Create Connection
                    </button>
                    <button 
                      onClick={handleCancelConnection}
                      className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {connections.length > 0 && (
            <div className="insights-container">
              <h4 className="insights-title">Architecture Insights</h4>
              <div className="insights-grid">
                <div className="insight-card">
                  <div className="insight-value">{canvasElements.length}</div>
                  <div className="insight-label">Components</div>
                </div>
                <div className="insight-card">
                  <div className="insight-value">{connections.length}</div>
                  <div className="insight-label">Connections</div>
                </div>
                <div className="insight-card">
                  <div className="insight-value">{new Set(connections.map(c => c.type)).size}</div>
                  <div className="insight-label">Connection Types</div>
                </div>
                <div className="insight-card">
                  <div className="insight-value">
                    {Object.values(componentCapabilities).reduce((sum, caps) => sum + caps.length, 0)}
                  </div>
                  <div className="insight-label">Deployed Capabilities</div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state-card">
          <h3 className="empty-state-title">No Department Selected</h3>
          <p className="empty-state-text">
            Select a department from the sidebar to start building their technology architecture.
          </p>
        </div>
      )}
    </div>
  );
}

export default Dashboard;